import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { versionRoles } from './versions.js';
import { pickScenarioBranch, resolveScenarioOptions } from './scenario.js';
import { parse } from 'mathjs';
import { valuesMatchWithinEpsilon } from './numerical.js';
import { seedsAndEquations, variableDomain } from './formula-extrema.js';
import { buildScopeWithValues, evaluateFormulaRaw, preprocessFormula, probeValuesForRecalls } from './formula-scope.js';
import { getEquationVariableNames, resolveMatchedBranch, activeFeedbackForBranch } from './conditional-branch.js';
// Re-exported so mcq.ts/keyword.ts/fitb.ts's existing `import ... from './dropdown.js'` lines
// (package-relative, not through index.ts) keep working unmodified — the condition-matching/
// coverage engine itself now lives in conditional-branch.ts since it's payload-agnostic (Ordering's
// order-branches reuse it too, see ordering.ts).
export * from './conditional-branch.js';
/**
 * Resolves `options` against the first matching branch (if any), producing a plain option list
 * exactly like today's statically-authored one — every downstream reader (evaluation, eval-lock
 * derivation, the chip legend) only ever needs to read `.role` and never needs to know whether it
 * came from a static field or a resolved branch. This is the one seam the whole feature hinges
 * on: introduce this function, nothing else needs to change shape.
 *
 * Deliberately does NOT fall back to each option's static `role` once branching is enabled but
 * unresolved (empty/invalid equation, or a coverage gap) — that would silently reuse whatever this
 * dropdown behaved like *before* branching existed, content the teacher never reviewed in the
 * context of this specific failure, and which might still carry a real `'correct'` role. Every
 * option becomes `'neutral'` instead — combined with evaluateDropdown's correct-first-terminal
 * check, that guarantees no student can ever pass through an unreviewed gap. Failing loudly
 * (impossible to answer correctly) beats failing open (a free pass nobody notices for years).
 */
export function resolveEffectiveOptions(options, equation, branches, resolvedVariables, angleMode = 'degrees') {
    if (!branches || branches.length === 0)
        return options;
    const matched = resolveMatchedBranch(equation, branches, resolvedVariables, angleMode);
    if (!matched)
        return options.map(o => ({ ...o, role: 'neutral' }));
    // The SAME merge a version applies — an item the key does not mention keeps its own role and
    // its own identity. One function, so a branch and a version can never resolve differently.
    return versionRoles(options, matched.roles);
}
// ─── Authoring-time warnings ────────────────────────────────────────────────────
/** A branch whose role snapshot doesn't have exactly one option marked correct — "an equation
 * resolution that won't give a correct setup," regardless of which value the variable resolves
 * to. Zero correct options means that branch can never be answered correctly at all; more than
 * one means evaluateDropdown's correct-first-terminal check could fire on more than one option,
 * which is meaningless for a single-select control. */
export function branchRoleIssue(branch, options) {
    const correctCount = options.filter(o => branch.roles[o.id] === 'correct').length;
    if (correctCount === 0)
        return 'none-correct';
    if (correctCount > 1)
        return 'multiple-correct';
    return null;
}
/** Compiles `formula` once — mathjs's `parse` is the expensive part of evaluation, and
 * checkOptionFormulaIssues below calls `.evaluate()` potentially thousands of times per option
 * while brute-force-sampling a variable's whole declared range, so paying the parse cost per
 * sample rather than once would make that enumeration far slower than it needs to be. Same
 * "never throw, report unresolvable" contract as evaluateEquation — just factored so the two
 * costs (parse vs. evaluate) can be paid separately. */
function compileFormula(formula, angleMode) {
    const names = getEquationVariableNames(formula);
    let node;
    try {
        // `preprocessFormula`, not a rewrite of its own: an option formula may quote an earlier
        // answer, and a hand-rolled strip knows only `{chips}`. Four copies of this rewrite is how
        // one surface came to disagree with another about what the teacher wrote.
        node = parse(preprocessFormula(formula));
    }
    catch {
        return null;
    }
    return {
        names,
        evaluate: (vars) => {
            if (names.some(n => vars[n] === undefined))
                return null;
            try {
                const v = node.evaluate(buildScopeWithValues(angleMode, vars));
                return typeof v === 'number' && Number.isFinite(v) ? v : null;
            }
            catch {
                return null;
            }
        },
    };
}
/** Caps the brute-force enumeration below at a fixed number of sampled variable-value
 * combinations — a question referencing several wide-range variables could otherwise blow up
 * combinatorially. Capped, not skipped entirely, so a check still runs on a bounded sample;
 * callers should surface `truncated` rather than silently presenting a capped check as exhaustive. */
export const MAX_OPTION_FORMULA_COMBINATIONS = 5000;
/**
 * Brute-force-checks every option with a `formula` (see DropdownBlockData.optionsAsEquations) for
 * two problems, across the full declared range of every variable any of those formulas reference
 * — exact, not sampled/approximate, in the same sense checkBranchCoverage's threshold reasoning is
 * exact: Agavi variables always have a declared min/max/step (never truly continuous), so the
 * domain is finite and fully enumerable, just potentially large when several variables are
 * involved (hence the cap above).
 *
 * Runs regardless of which option currently holds which `role` — a branch's role map can mark any
 * option correct depending on which branch resolves, so there's no "safe to skip" option to
 * exclude from either check.
 *
 * Generic over any `{id, formula?}`-shaped array (not just `DropdownOption[]`) — it never reads
 * `.text`/`.role`/anything else, so a block with no such concepts at all (Ordering's `OrderingItem`,
 * which has no per-option correctness) can reuse this verbatim for its own item-level equation mode.
 */
export function checkOptionFormulaIssues(options, variables, 
/**
 * One mode, or a LOOKUP for callers whose options each carry their own.
 *
 * Angle mode is per formula field now, and every option's formula is its own field, so a
 * single mode cannot speak for a list of them. The scalar form stays because most callers —
 * and every test — have exactly one mode to give.
 */
angleMode = 'degrees') {
    const modeOf = typeof angleMode === 'function' ? angleMode : () => angleMode;
    const withFormula = options.filter(o => o.formula && o.formula.trim() !== '');
    if (withFormula.length === 0)
        return { issues: [], truncated: false };
    // A formula that fails to even PARSE (a bare `+`, unbalanced parens, ...) is exactly as
    // unevaluable as one that parses fine but fails at evaluation time (an undefined variable, a
    // domain error) — both mean "this option has no reachable value," the same severity this whole
    // check exists to catch. Previously a parse failure silently dropped the option out of `compiled`
    // via the old `.filter(c => c.compiled !== null)` and was never flagged at all; now it's added to
    // `unevaluableIds` directly instead of being discarded.
    const unevaluableIds = new Set();
    const compiled = [];
    for (const o of withFormula) {
        const c = compileFormula(o.formula, modeOf(o.id));
        if (c === null)
            unevaluableIds.add(o.id);
        else
            compiled.push({ id: o.id, compiled: c });
    }
    /*
     * An option formula may reference an EQUATION, which has no domain of its own, so what gets
     * enumerated is the seeds it ultimately stands on — the same rule `forEachRoll` follows, for the
     * same reason: equations must cost the cross-product nothing or the budget disappears.
     *
     * A CONSTANT is a seed with a single value. Before, the filter below demanded a finite
     * min/max/step, so a constant was dropped, its name was then missing from every combination, and
     * `compiled.evaluate()` reported the option as UNEVALUABLE — a wrong error rather than a missing
     * feature.
     *
     * This is a fourth hand-written odometer, which `forEachRoll` exists to stop. It is not ported
     * because the two genuinely differ at the budget: this one reports the collisions it did find
     * and says `truncated`, where `forEachRoll` declines to claim anything. Both are right for their
     * callers — a collision found is real whether or not the sweep finished, while a partial sweep
     * gives a wrong EXTREMUM. Unifying means making that an argument, not flattening it.
     */
    const referencedNames = new Set(compiled.flatMap(c => c.compiled.names));
    // `blocked` is deliberately ignored: a name nothing defines is what `compiled.evaluate()`
    // already reports as unevaluable, and that verdict is better than one invented here.
    const { seeds: relevantVars, equations } = seedsAndEquations(referencedNames, variables);
    let combos = [{}];
    let truncated = false;
    for (const v of relevantVars) {
        const domain = variableDomain(v);
        if (domain.length === 0)
            continue;
        const next = [];
        outer: for (const combo of combos) {
            for (const value of domain) {
                if (next.length >= MAX_OPTION_FORMULA_COMBINATIONS) {
                    truncated = true;
                    break outer;
                }
                next.push({ ...combo, [v.name]: value });
            }
        }
        combos = next;
        if (truncated)
            break;
    }
    // Each equation is computed per combination, in dependency order, and joins the scope the option
    // formulas are evaluated against. An equation that fails for a combination simply does not
    // appear, and the options standing on it are reported unevaluable — which is what they are.
    if (equations.length > 0) {
        for (const combo of combos) {
            for (const eq of equations) {
                const scope = {};
                for (const [name, value] of Object.entries(combo))
                    scope[name] = String(value);
                // The variable equation's own mode: it belongs to the variable, not to the options.
                const computed = evaluateFormulaRaw(eq.formula, scope, eq.angleMode ?? 'degrees');
                if (isFinite(computed))
                    combo[eq.name] = computed;
            }
        }
    }
    /*
     * A RECALLED ANSWER STANDS IN AT 1, which is `probeValuesForRecalls`' whole job.
     *
     * `compileFormula` has always rewritten `[ara_…]` into a symbol — its own comment says an
     * option formula may quote an earlier answer — and nothing ever put a VALUE under that
     * symbol here, so mathjs threw on an undefined name and every such option was reported
     * "will not evaluate". Reported against a plainly numeric alias: *"i'm getting an error of
     * 'answer option's equation will not evaluate' when i select [eaten], which should be a
     * plain number and seems to evaluate fine in the testing block"* — and it did, because the
     * test surface supplies the real answer and only this probe had nothing.
     *
     * The question this check asks is "has this option a reachable value at all", not what the
     * value is, so any finite number does. 1 rather than 0, so `x/[ref]` is not reported as a
     * division by zero the probe invented.
     */
    const recallIds = new Set();
    for (const o of withFormula) {
        for (const id of Object.keys(probeValuesForRecalls(o.formula)))
            recallIds.add(id);
    }
    const withRecall = new Set(withFormula
        .filter(o => Object.keys(probeValuesForRecalls(o.formula)).length > 0).map(o => o.id));
    const standIns = {};
    for (const id of recallIds)
        standIns[id] = 1;
    const collisionPairs = new Set();
    for (const combo of combos) {
        const scope = recallIds.size > 0 ? { ...combo, ...standIns } : combo;
        const values = [];
        for (const { id, compiled: c } of compiled) {
            const v = c.evaluate(scope);
            if (v === null)
                unevaluableIds.add(id);
            /*
             * A RECALL-BEARING OPTION IS NOT IN THE COLLISION SWEEP. Its value is the student's and
             * is not known here; the stand-in above makes every one of them 1, so two perfectly
             * distinct options would be reported as identical — an error invented by the probe,
             * which is the thing the stand-in exists to avoid.
             */
            else if (!withRecall.has(id))
                values.push({ id, value: v });
        }
        for (let i = 0; i < values.length; i++) {
            for (let j = i + 1; j < values.length; j++) {
                if (valuesMatchWithinEpsilon(values[i].value, values[j].value)) {
                    collisionPairs.add([values[i].id, values[j].id].sort().join('|'));
                }
            }
        }
    }
    const issues = [
        ...Array.from(unevaluableIds).map(id => ({ kind: 'unevaluable', optionIds: [id] })),
        ...Array.from(collisionPairs).map(pair => ({ kind: 'collision', optionIds: pair.split('|') })),
    ];
    return { issues, truncated };
}
/**
 * Returns the feedback states that match a student's answer.
 *
 * Same evaluation sequence as MCQ's single-select case, simplified for the
 * fact that a dropdown can only ever have one option selected at a time:
 * 1. optional mode → any_response, answered or not — leaving it blank is just as acceptable an
 *    outcome as picking something, so unlike graded/survey there's no separate "you didn't
 *    answer" state carved out for this mode, and no correctness check either.
 * 2. No selection (graded/survey only) → unanswered state
 * 3. survey mode → any_answer (any selection counts as participation, no correctness involved)
 * 4. graded mode → correct-first-terminal: if the selected option is correct,
 *    `correct` wins outright, full stop — otherwise the targeted feedback for
 *    the selected option fires if one exists, else `incorrect`
 *
 * `correct`/`any_answer`/`any_response` are three distinct trigger names for what's conceptually
 * "the one positive-outcome state," not the same trigger reused across modes — see
 * positiveTriggerForMode in use-input-block-feedback.ts for why: a raw `correct_any` string
 * firing in survey mode doesn't mean "correct" at all, it means "answered something," which is
 * exactly the kind of thing a name should say plainly rather than needing gradingMode alongside
 * it to disambiguate.
 *
 * Correct-first-terminal means stale feedback left over from before an option was
 * marked correct can never hijack the outcome (it's simply never consulted, not
 * just suppressed). Dropdown never produces a neutral-role option (its role
 * cycle is a strict correct/incorrect radio toggle), so unlike MCQ multi-select
 * there's no "neutral floats free" nuance to worry about here.
 *
 * Targeted trigger rule:
 *   option_X fires when option X is selected (a non-correct option was chosen)
 *
 * There is no option_X_absent trigger (unlike MCQ) — that only makes sense
 * when multiple options can be required at once, which single-select can't do.
 *
 * `resolvedVariables` resolves `conditionalBranches` (see ConditionalRoleBranch above) — optional,
 * and a no-op whenever `blockData.conditionalBranches` is unset, which is every dropdown that
 * doesn't use this feature. When a branch resolves, its OWN feedback (noAnswer/correct/incorrect/
 * targeted) is what fires — not the block's top-level `feedback` — since a branch fundamentally
 * changes which option is correct, its messaging has to be able to differ too. The top-level
 * feedback only fires when branching isn't enabled at all, or when it is but nothing resolved
 * (see resolveEffectiveOptions' doc comment on why that's forced to all-neutral roles rather than
 * silently reusing static ones).
 *
 * Branches are consulted ONLY in `graded` mode — they're authored exclusively there (every branch's
 * `feedback` only ever contains graded-shaped triggers: correct/incorrect/unanswered/targeted, never
 * `any_answer`/`any_response`), and switching `gradingMode` away from `'graded'` does NOT clear
 * `conditionalBranches`/`conditionalEquation` (deliberately — a teacher cycling back to graded
 * shouldn't lose their branch setup). Without this gate, a still-matching leftover branch would get
 * consulted in survey/optional mode too, and since its feedback has no `any_answer`/`any_response`
 * state to find, the student would silently get NO feedback at all. Survey/optional always read the
 * block's own top-level `feedback` — the "static setup appropriate for each category" — regardless
 * of whatever branches happen to be sitting in the data.
 */
export function evaluateDropdown(answer, blockData, resolvedVariables = {}, 
/**
 * The leader's variant in force, for a block that follows one — see `linked-scenarios.md`.
 *
 * A parameter rather than a field because it is RUN-TIME context, exactly like
 * `resolvedVariables`: the same stored block grades differently per attempt depending on what
 * the task drew. Resolve it with `scenarioInForce` first, so a stale id lands on the same
 * variant the leader is showing.
 */
scenarioId) {
    const { selectedId } = answer;
    const { gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches, angleMode } = blockData;
    /*
     * A FOLLOWER'S SCENARIO ROW REPLACES the numeric branch, never joins it. The two are separate
     * selectors and a block answers to one; linking disables numeric branching exactly as it
     * disables versions. Checked by presence rather than by a flag, the same way `variableKind`
     * reads a variable's shape — two field signatures cannot disagree with themselves.
     */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const options = gradingMode !== 'graded' ? blockData.options
        : followed ? resolveScenarioOptions(blockData.options, scenarioBranches, scenarioId, resolvedVariables, angleMode ?? 'degrees')
            : resolveEffectiveOptions(blockData.options, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    // Optional chaining, not a plain ternary — a branch saved before `feedback` was added to
    // ConditionalRoleBranch has no such field at all; falling back to the block's own top-level
    // feedback here is safer than crashing on `.states` of `undefined`.
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer always lands on Default Incorrect instead of a leftover targeted state firing
    // terminally on an option that was just declared neutral. See activeFeedbackForBranch.
    const activeFeedback = activeFeedbackForBranch((scenarioRow ?? matchedBranch)?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    /** The targeted state this selection fires. ONE expression, used by every grading mode — see
     *  `participationOutcome`. A second copy that merely agreed today is what this avoids. */
    const targetedFor = (id) => activeFeedback.states.filter(st => st.type === 'targeted' && st.trigger === `option_${id}`);
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    if (!selectedId) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedFor(selectedId));
    }
    // Graded: correct is checked first and is terminal — see doc comment above.
    const selectedOption = options.find(o => o.id === selectedId);
    if (selectedOption?.role === 'correct') {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matchedTargeted = targetedFor(selectedId);
    if (matchedTargeted.length > 0)
        return matchedTargeted;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
//# sourceMappingURL=dropdown.js.map